import { z } from 'zod';

import {
  VisibilityModeSchema,
  isVisibilityMode,
  type VisibilityMode,
} from '../visibility-mode/visibility-mode';
import { type ViewerContext } from '../viewer-context/viewer-context';

/**
 * The four columns a question carries for the visibility engine (A3: columns on
 * the question, not a policy table — `03-visibility-policy.md`).
 *
 * Two modes and two defaults, one pair per context. The defaults only ever
 * matter under `user_controlled`; they are stored unconditionally so switching a
 * question from forced to member-controlled does not have to invent one.
 */
export const QuestionVisibilityPolicySchema = z
  .object({
    publicVisibility: VisibilityModeSchema,
    matchedVisibility: VisibilityModeSchema,
    defaultVisiblePublic: z.boolean(),
    defaultVisibleMatched: z.boolean(),
  })
  .strict();

export type QuestionVisibilityPolicy = z.infer<typeof QuestionVisibilityPolicySchema>;

/**
 * **The** visibility evaluator (N18) — the single implementation of the
 * algorithm in `03-visibility-policy.md`, living in contracts for the same
 * reason `hasPermission` does (N22): the api resolver, the admin builder's
 * preview (P10) and the mobile privacy screen (P12) must all answer this
 * question identically, and three copies of a boolean rule drift.
 *
 * ```
 * policy = context == 'matched' ? matchedVisibility : publicVisibility
 * forced_visible  → SHOW
 * forced_hidden   → HIDE
 * user_controlled → preference ?? defaultVisible<Context>
 * ```
 *
 * A `preference` of `null` **and** `undefined` both mean "the member has not
 * touched this" — a repository that returns `undefined` for a missing row and a
 * caller that normalizes to `null` must not resolve differently.
 *
 * The `switch` is **exhaustive over `VisibilityMode`**, so adding a mode is a
 * compile error here rather than a silent fallthrough; the runtime default is
 * still `false`, because a stored value that is not a declared mode carries no
 * policy at all and "no policy" can only safely mean hidden.
 *
 * Pure and synchronous on purpose: it takes the question's policy and the
 * owner's preference as data, so it can be applied to a whole page of answers
 * in one pass with no I/O per field.
 */
export function resolveVisibility(
  policy: QuestionVisibilityPolicy,
  context: ViewerContext,
  preference: boolean | null | undefined,
): boolean {
  const mode = visibilityModeFor(policy, context);

  // Fail-closed ahead of the switch: a hand-edited row, or a mode a later
  // migration retired, must hide rather than reach the `user_controlled` branch
  // and be governed by a member preference that no longer means anything.
  if (!isVisibilityMode(mode)) {
    return false;
  }

  switch (mode) {
    case 'forced_visible':
      return true;

    case 'forced_hidden':
      return false;

    case 'user_controlled':
      return preference ?? defaultVisibleFor(policy, context);
  }
}

/** The mode governing this context — the one place the two columns are chosen between. */
export function visibilityModeFor(
  policy: QuestionVisibilityPolicy,
  context: ViewerContext,
): VisibilityMode {
  return context === 'matched' ? policy.matchedVisibility : policy.publicVisibility;
}

/** The staff-chosen default for this context, used only under `user_controlled`. */
export function defaultVisibleFor(
  policy: QuestionVisibilityPolicy,
  context: ViewerContext,
): boolean {
  return context === 'matched' ? policy.defaultVisibleMatched : policy.defaultVisiblePublic;
}

/**
 * Whether the member may decide this field in this context — i.e. whether the
 * privacy screen renders a switch or a lock (P12).
 *
 * The same predicate guards the **write** side: a preference row for a field
 * that is not member-controlled is refused rather than stored, so a policy later
 * relaxed to `user_controlled` cannot resurrect a stale toggle nobody made
 * knowingly.
 */
export function isMemberControllable(
  policy: QuestionVisibilityPolicy,
  context: ViewerContext,
): boolean {
  return visibilityModeFor(policy, context) === 'user_controlled';
}
